/**
 * The months a student can be collected for, and what each one currently owes.
 *
 * A month — not an invoice — is the unit the collector works in. The backend takes a
 * `months` array, settles the matching invoices oldest-first, and raises an invoice for
 * a month that was never billed (paying ahead). So the grid has to describe months that
 * have an invoice and months that don't, which is why an unbilled month's amount is an
 * estimate: the endpoint bills as a side effect of collecting and must never be called
 * to price a month speculatively.
 */

import type { FeeStructureRecord } from '../../services/feeStructureApi';
import type { FeeAllocation } from '../../services/transactionApi';
import { buildFeeBreakdown } from '../../utils/feeBreakdown';
import type { FeeBreakdown, FeeCharge, FeeDiscount } from '../../utils/feeBreakdown';
import type { ReceiptData } from '../../utils/printReceipt';

/** `paid` / `partial` / `unpaid` are billed months; `unbilled` has no invoice yet. */
export type MonthStatus = 'paid' | 'partial' | 'unpaid' | 'unbilled';

export interface MonthCell {
    /** YYYY-MM — exactly the form the API's `months` array takes. */
    month: string;
    /** "Jul 2026" */
    label: string;
    /** "Jul" */
    short: string;
    year: number;
    status: MonthStatus;
    /** What selecting this month adds to the amount to collect. */
    due: number;
    /** `due` is a fee-structure estimate rather than a billed figure. */
    estimated: boolean;
    /** Invoices issued in this month. Empty when the month is unbilled. */
    invoiceIds: string[];
}

/** The invoice fields the grid reads. */
export interface MonthInvoice {
    id: string;
    issue_date: string;
    paid_amount: string;
    balance_amount: string;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const round2 = (n: number): number => Math.round(n * 100) / 100;

const num = (v: string | number | null | undefined): number => {
    if (v === null || v === undefined || v === '') return 0;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/** The YYYY-MM a date-only string falls in. Sliced rather than parsed through `Date`,
    so a timezone behind UTC can't drag the month backwards. */
export function monthKey(iso: string): string {
    return iso.slice(0, 7);
}

/** "2026-07" → "Jul 2026". */
export function formatMonth(month: string): string {
    const [y, m] = month.split('-');
    const name = MONTHS_SHORT[Number(m) - 1];
    return name ? `${name} ${y}` : month;
}

/** The month after this one. */
export function nextMonth(month: string): string {
    let [y, m] = month.split('-').map(Number);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    return `${y}-${String(m).padStart(2, '0')}`;
}

/** Every month from `start` to `end` inclusive — an academic session's span. */
export function sessionMonths(start: string, end: string): string[] {
    const [sy, sm] = start.slice(0, 7).split('-').map(Number);
    const [ey, em] = end.slice(0, 7).split('-').map(Number);
    if (!sy || !sm || !ey || !em) return [];

    const out: string[] = [];
    let cur = `${sy}-${String(sm).padStart(2, '0')}`;
    const last = `${ey}-${String(em).padStart(2, '0')}`;
    // A reversed or malformed range would otherwise spin forever; a session is ~12 months.
    while (cur <= last && out.length < 24) {
        out.push(cur);
        cur = nextMonth(cur);
    }
    return out;
}

/** The period a receipt covers: "Jul 2026", "May–Jul 2026", "Dec 2025 – Feb 2026".
    A gap in the selection is listed rather than bridged, so a range never claims a
    month that wasn't paid for. */
export function formatMonthRange(months: string[]): string {
    const sorted = [...new Set(months)].sort();
    if (sorted.length === 0) return '';
    if (sorted.length === 1) return formatMonth(sorted[0]);

    const contiguous = sorted.every((m, i) => i === 0 || nextMonth(sorted[i - 1]) === m);
    if (!contiguous) return sorted.map(formatMonth).join(', ');

    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const sameYear = first.slice(0, 4) === last.slice(0, 4);
    return sameYear
        ? `${MONTHS_SHORT[Number(first.slice(5, 7)) - 1]}–${formatMonth(last)}`
        : `${formatMonth(first)} – ${formatMonth(last)}`;
}

/**
 * What an unbilled month would cost: the class's fee structure, and nothing else.
 *
 * Transport is deliberately *not* added, even for a student who rides a bus. The invoice the
 * backend raises for the month is the fee structure — it carries no transport item — so a
 * month priced with the fare folded in would ask for money no charge exists for, and the
 * surplus would land as an advance rather than as a transport payment. The fare is charged
 * as a transport line item instead (the desk prefills one from the student's route).
 *
 * Only ever an estimate — the real invoice is raised by the backend when the payment lands,
 * and it may differ (a mid-session fee change, a discount). The allocations that come back
 * are the authoritative record.
 */
export function estimateMonthlyFee(structure: FeeStructureRecord | undefined): number {
    const components = structure?.components ?? [];
    // Components are the truth when expanded; `total_amount` is the structure's own roll-up.
    const fee = components.length > 0
        ? components.reduce((sum, c) => sum + num(c.amount), 0)
        : num(structure?.total_amount);
    return round2(fee);
}

/**
 * One cell per month of the session, carrying what that month owes.
 *
 * A month can hold more than one invoice — an ad-hoc charge issued alongside the
 * monthly fee — and the backend settles every invoice the month contains. So the
 * month owes their combined balance, not just the monthly fee's.
 */
export function buildMonthCells(
    months: string[],
    invoices: MonthInvoice[],
    estimate: number,
): MonthCell[] {
    const byMonth = new Map<string, MonthInvoice[]>();
    for (const inv of invoices) {
        const key = monthKey(inv.issue_date);
        const found = byMonth.get(key);
        if (found) found.push(inv);
        else byMonth.set(key, [inv]);
    }

    return months.map((month) => {
        const base = {
            month,
            label: formatMonth(month),
            short: MONTHS_SHORT[Number(month.slice(5, 7)) - 1] ?? month,
            year: Number(month.slice(0, 4)),
        };

        const forMonth = byMonth.get(month) ?? [];
        if (forMonth.length === 0) {
            return { ...base, status: 'unbilled' as const, due: estimate, estimated: true, invoiceIds: [] };
        }

        const balance = round2(forMonth.reduce((sum, i) => sum + num(i.balance_amount), 0));
        const paid = round2(forMonth.reduce((sum, i) => sum + num(i.paid_amount), 0));
        const status: MonthStatus = balance <= 0 ? 'paid' : paid > 0 ? 'partial' : 'unpaid';

        return { ...base, status, due: balance, estimated: false, invoiceIds: forMonth.map((i) => i.id) };
    });
}

/** The total a selection of months asks for. */
export function monthsTotal(cells: MonthCell[], selected: string[]): number {
    const picked = new Set(selected);
    return round2(cells.reduce((sum, c) => (picked.has(c.month) ? sum + c.due : sum), 0));
}

/** One fee, summed over every month it was billed in. */
type MonthlyGroup = { name: string; gross: number; discount: number; months: string[] };

/** Fold one invoice line into its fee's running total, remembering the month it came from. */
function foldMonth<K>(
    groups: Map<K, MonthlyGroup>,
    key: K,
    name: string,
    month: string,
    gross: number,
    discount: number,
): void {
    const g = groups.get(key) ?? { name, gross: 0, discount: 0, months: [] };
    g.gross = round2(g.gross + gross);
    g.discount = round2(g.discount + discount);
    if (!g.months.includes(month)) g.months.push(month);
    groups.set(key, g);
}

/**
 * The receipt for a months payment, built from what the server actually billed.
 *
 * One line per *fee*, never per month: tuition paid for three months is a single
 * "Tuition — May–Jul 2026 ₹4,500" line, not the same fee repeated three times down the
 * page — a year paid in advance would otherwise run off the paper. Its discount is
 * combined the same way. A fee the collector added is likewise put back together: a ₹300
 * charge split three ways is billed as three ₹100 items, but the payer handed over ₹300
 * once and the receipt should say so. The items carry `line_item_index` precisely so they
 * can be regrouped without guessing.
 *
 * Returns null when the server sent no items, so the caller can fall back to the
 * allocation totals.
 */
export function breakdownFromAllocations(allocations: FeeAllocation[]): FeeBreakdown | null {
    if (!allocations.some((a) => a.items && a.items.length > 0)) return null;

    /* Oldest month first, so a combined line reads "May–Jul 2026" and not "Jul, May". */
    const byMonth = [...allocations].sort((a, b) => a.month.localeCompare(b.month));

    /* Fees the months were already carrying, keyed by the fee — the same head charged in
       three months is one group. Fees this payment added are keyed by the request row that
       created them, so a split fee's per-month fragments find each other. */
    const own = new Map<string, MonthlyGroup>();
    const added = new Map<number, MonthlyGroup>();

    for (const a of byMonth) {
        for (const item of a.items ?? []) {
            const name = item.description
                ? `${item.fee_head_name} — ${item.description}`
                : item.fee_head_name;
            const gross = num(item.gross_amount);
            const discount = num(item.discount_amount);
            if (item.line_item_index === null) foldMonth(own, name, name, a.month, gross, discount);
            else foldMonth(added, item.line_item_index, name, a.month, gross, discount);
        }
    }

    const charges: FeeCharge[] = [];
    const discounts: FeeDiscount[] = [];

    for (const g of own.values()) {
        const label = `${g.name} — ${formatMonthRange(g.months)}`;
        if (g.gross > 0) charges.push({ name: label, amount: g.gross });
        if (g.discount > 0) discounts.push({ name: `Discount — ${label}`, amount: g.discount });
    }

    for (const [, g] of [...added.entries()].sort((a, b) => a[0] - b[0])) {
        /* Name the span only when it took more than one month — "Lost Book (May–Jul 2026)". */
        const span = g.months.length > 1 ? ` (${formatMonthRange(g.months)})` : '';
        charges.push({ name: `${g.name}${span}`, amount: g.gross });
        if (g.discount > 0) discounts.push({ name: `Discount — ${g.name}`, amount: g.discount });
    }

    return buildFeeBreakdown(charges, discounts);
}

/**
 * What a payment settled, when the server named no lines: one row per invoice title,
 * combined across the months it was charged in. Amounts are what each month *received*,
 * not what it was billed — it's all such a server tells us.
 */
function breakdownFromTotals(allocations: FeeAllocation[]): FeeBreakdown {
    const groups = new Map<string, MonthlyGroup>();
    for (const a of [...allocations].sort((x, y) => x.month.localeCompare(y.month))) {
        foldMonth(groups, a.title, a.title, a.month, num(a.amount_allocated), 0);
    }
    return buildFeeBreakdown(
        [...groups.values()].map((g) => ({
            name: `${g.name} — ${formatMonthRange(g.months)}`,
            amount: g.gross,
        })),
        [],
    );
}

/** The receipt fields a months payment determines. */
export type MonthsReceiptParts = Pick<
    ReceiptData,
    'invoiceNumber' | 'periodLabel' | 'items' | 'discounts' | 'subtotal' | 'totalAmount' | 'balanceDue' | 'advanceAmount'
>;

/**
 * Turn a payment's `allocations` into a receipt — the months it covered, what they were
 * billed, and what they still owe.
 *
 * Shared by the desk (printing the payment it just took) and the ledger (reprinting one
 * from the record), so a reprint is the same paper as the original. Both read the server's
 * allocations rather than the months that were *asked* for: a short payment runs out before
 * the last months and leaves them out of the list entirely, and this must never claim a
 * month the money never reached.
 */
export function monthsReceiptParts(allocations: FeeAllocation[], unallocated: number): MonthsReceiptParts {
    /* Prefer what the server billed: its items let a split fee print as the one charge the
       payer handed money over for, rather than as three fragments. A server that sends no
       items falls back to the per-month amounts that were settled. */
    const breakdown = breakdownFromAllocations(allocations) ?? breakdownFromTotals(allocations);

    return {
        invoiceNumber: allocations.map((a) => a.invoice_number).join(', ') || '—',
        periodLabel: formatMonthRange(allocations.map((a) => a.month)),
        items: breakdown.charges,
        discounts: breakdown.discounts,
        subtotal: breakdown.subtotal,
        totalAmount: breakdown.total,
        // What the invoices this payment touched still owe — the server's figure, not
        // total − paid, which would ignore anything already paid on the same months.
        balanceDue: round2(allocations.reduce((sum, a) => sum + num(a.invoice_balance_amount), 0)),
        advanceAmount: unallocated,
    };
}
